/**
 * COMMS.app's multi-shot grouping (`comms-attack-reaction-cards` 3.3) — the pure half, kept out of
 * {@link import("./CommsApp").CommsApp} so it is unit-testable without React.
 *
 * A Barrage or a Skirmish-with-Auxiliary commits one resolution per shot, so the feed holds one
 * entry per shot (append-only, `decisions/0040`; shot 2 can never be folded into shot 1's entry).
 * This is rendering over those entries: consecutive shots of one action by one actor show under one
 * group header.
 *
 * **The key is the record, never adjacency alone.** Each shot's `about.shot = { action, index }`
 * (stamped off the open `AttackLock`) says which shot it was; index 1 opens a group, and each later
 * shot joins only if it is the same actor's same action at the next index. Another combatant's
 * reaction can resolve between two shots (a Brace, an Overwatch), and its outcome line or card lands
 * in the feed between them — measured: a Brace decision line is written BEFORE the resolution of the
 * shot it answers. Those system entries between shots render AFTER the group, in their own order, so
 * the group stays intact. A player-authored entry (chat, a `/roll`, an emote) between shots ends the
 * group rather than being moved. An entry without `about.shot` (every entry persisted before 1.1)
 * renders alone, and a group of one is just that entry.
 */
import type { FeedEntry, SystemFeedEntry } from "@lancer-vtt/shared";

/** One thing the feed renders: a lone entry, or a multi-shot group (two or more shots). */
export type FeedItem = { kind: "entry"; entry: FeedEntry } | { kind: "group"; shots: SystemFeedEntry[] };

type ShotKey = { actor: string; action: "barrage" | "skirmish"; index: number };

function shotOf(entry: FeedEntry): ShotKey | undefined {
  if (entry.kind !== "system") return undefined;
  const about = entry.resolution?.about;
  if (about?.shot === undefined) return undefined;
  return { actor: about.actor, action: about.shot.action, index: about.shot.index };
}

/** Group a (filtered, visible) feed into render items. Order is preserved except for system entries between grouped shots, which follow the group. */
export function groupShots(entries: readonly FeedEntry[]): FeedItem[] {
  const out: FeedItem[] = [];
  let i = 0;
  while (i < entries.length) {
    const opener = entries[i]!;
    const key = shotOf(opener);
    if (key === undefined || key.index !== 1) {
      out.push({ kind: "entry", entry: opener });
      i++;
      continue;
    }
    const shots = [opener as SystemFeedEntry];
    const between: FeedEntry[] = [];
    let pending: FeedEntry[] = [];
    let j = i + 1;
    while (j < entries.length) {
      const next = entries[j]!;
      const s = shotOf(next);
      if (s !== undefined && s.actor === key.actor && s.action === key.action && s.index === shots.length + 1) {
        shots.push(next as SystemFeedEntry);
        between.push(...pending);
        pending = [];
        j++;
        continue;
      }
      // A system entry that is not a shot may sit between two shots; a new shot (of any group)
      // or anything player-authored ends the scan.
      if (next.kind !== "system" || s !== undefined) break;
      pending.push(next);
      j++;
    }
    out.push(shots.length > 1 ? { kind: "group", shots } : { kind: "entry", entry: opener });
    for (const entry of between) out.push({ kind: "entry", entry });
    // Entries scanned after the last shot were not moved; they render in place from here.
    i = j - pending.length;
  }
  return out;
}
